import { RequestError } from "../requestError";
import api from "../axiosBase";
import { getURL } from "../serviceHelper";
import { getEnv } from "../../AppConfigs";
import { IDocumentContent } from "../../models/IDocumentContent";
import { ISharepointLink, ISharepointLinkTypes } from "../../models/ISharepoint";
import { IComment } from "../../models/IComment";
import { EditorContentPermissionType } from "../../pages/document/editor-content-permission-context";

const SPSiteId = getEnv("REACT_APP_SP_SITE_ID");
const SPListId = getEnv("REACT_APP_DOCUMENT_CONTENTS_LIST_ID");

const expand =
  "expand=fields(select=id,Title,Content,IsContent,TemplateLookupId,FileId,Position,CustomId,CustomParentId,ContentSummary,Position,Permissions,ShareLinks,Comments,Modified, numberEdit, diagramJson, editorVersiona, publishVersion, UserPermissions, prepareVersion, isQuote)";
const commentExpand = "expand=fields(select=Comments)";

const orderby = "&$orderby=fields/Position,id";

export class DocumentContentService {
  static async getList(templateId: any = null, fileId: any = null) {
    const filters: Array<string> = [];
    if (templateId) {
      filters.push(`TemplateLookupId eq ${templateId}`);
    } else if (fileId) {
      filters.push(`FileId eq '${fileId}'`);
    } else {
      return [];
    }

    let filter = "";
    if (filters.length > 0) {
      filter = "&filter=fields/" + filters.join(" AND ");
    }

    const url = `/sites/${SPSiteId}/lists/${SPListId}/items?${expand}${filter}${orderby}&top=3000`;

    return api
      .get(getURL(url))
      .then((res) => {
        return res.data?.value?.map((item: any) => this.mapData(item));
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getFileId_OldContentVersion(CustomId: string) {
    const url = `/sites/${SPSiteId}/lists/${SPListId}/items?expand=fields(select=CustomId,FileId,publishVersion,Permissions)&filter=fields/CustomId eq '${CustomId}'`;
    return api
      .get(getURL(url))
      .then((res) => {
        return res?.data?.value;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getFileIds_OldContentVersion(CustomIds: string[]) {
    const customIds = CustomIds; // Example array of customIds

    // Constructing the filter string dynamically for each customId
    const filters = customIds.map((customId) => `fields/CustomId eq '${customId}'`);

    // Joining the filters with 'or' operator to combine them
    const filterString = filters.join(" or ");
    const url = `/sites/${SPSiteId}/lists/${SPListId}/items?expand=fields(select=CustomId,FileId,publishVersion,Permissions)&filter=${filterString}`;
    return api
      .get(getURL(url))
      .then((res) => {
        return res?.data?.value;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getCommentList(fileId: any = null) {
    const filters: Array<string> = [];
    if (fileId) {
      filters.push(`FileId eq '${fileId}'`);
    } else {
      return [];
    }

    let filter = "";
    if (filters.length > 0) {
      filter = "(&filter=fields/Comments ne null) And " + filters;
    }

    const url = `/sites/${SPSiteId}/lists/${SPListId}/items?${commentExpand}${filter}`;

    return api
      .get(getURL(url))
      .then((res) => {
        return res.data?.value?.map((item: any) => this.mapData(item));
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async cloneFromTempalteToFile(
    sourceId: any = null,
    fileId: any = null,
    isSourceFile: any = false,
    docPublishVersion?: any,
    docEditionVersion?: any,
    permissionContent: EditorContentPermissionType[] = [],
  ) {
    const contents = isSourceFile ? await this.getList(null, sourceId) : await this.getList(sourceId);
    if (contents.length > 0) {
      const infoPromises: any[] = [];
      let index = 1;
      contents.forEach((item: any) => {
        const { Modified, ...fields } = item;
        fields.Position = index;
        fields.Id = undefined;
        fields.TemplateId = undefined;
        fields.FileId = fileId;
        fields.ShareLinks = item.ShareLinks ? JSON.stringify(item.ShareLinks) : undefined;
        fields.CreatedUser = undefined;
        fields.ModifiedUser = undefined;
        fields.Modified = undefined;
        fields.numberEdit = 0;
        fields.editorVersiona = docEditionVersion;
        fields.publishVersion = docPublishVersion;
        fields.Permissions = permissionContent.find((e) => e.customId === fields.CustomId)?.permissions || undefined;
        index = index + 1;
        const { UserPermissions, ...clonedFields } = fields;
        infoPromises.push(this.create(clonedFields));
      });
      Promise.all(infoPromises);
    }
  }

  static async get(id: any) {
    const url = `/sites/${SPSiteId}/lists/${SPListId}/items/${id}?${expand}`;
    try {
      const response = await api.get(getURL(url));
      if (response.data) {
        return response.data.fields.numberEdit;
      }
      return 0; // Default value if response.data.fields.numberEdit is undefined
    } catch (err) {
      throw new RequestError(err);
    }
  }

  static async getComment(id: any) {
    const url = `/sites/${SPSiteId}/lists/${SPListId}/items?${commentExpand}&filter=fields/id eq ${id}`;
    return api
      .get(getURL(url))
      .then((res) => {
        if (res.data.length > 0) return this.mapData(res.data[0]);
        return null;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async create(fields: any) {
    return api
      .post(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items`), { fields })
      .then((res) => this.mapData(res.data))
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async update(id: any, fields: any) {
    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items/${id}/fields`), fields)
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updatePermissions(id: any, permissions: ISharepointLinkTypes) {
    const fields = {
      Permissions: JSON.stringify(permissions),
    };
    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items/${id}/fields`), fields)
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateShareLinks(id: any, shareLinks: Array<ISharepointLink>) {
    const fields = {
      ShareLinks: JSON.stringify(shareLinks),
    };
    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items/${id}/fields`), fields)
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateComment(id: any, comments: Array<IComment>) {
    const fields = {
      Comments: JSON.stringify(comments),
    };
    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items/${id}/fields`), fields)
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async delete(id: any) {
    return api
      .delete(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items/${id}`))
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getHistory(id: any) {
    return api.get(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items/${id}/versions`));
  }

  static mapData(item: any) {
    const result: IDocumentContent = {
      Id: item?.fields.id,
      CustomId: item?.fields.CustomId,
      Title: item?.fields.Title,
      IsContent: item?.fields.IsContent,
      Content: item?.fields.Content,
      ContentSummary: item?.fields.ContentSummary,
      CustomParentId: item?.fields.CustomParentId,
      TemplateLookupId: item?.fields.TemplateLookId,
      FileId: item?.fields.FileId,
      Position: item?.fields.Position ?? item?.fields.id,
      Permissions: item?.fields.Permissions ? JSON.parse(item?.fields.Permissions) : undefined,
      ShareLinks: item?.fields.ShareLinks ? JSON.parse(item?.fields.ShareLinks) : undefined,
      CreatedUser: item?.createdBy?.user,
      ModifiedUser: item?.lastModifiedBy?.user,
      Comments: item?.fields.Comments,
      Modified: item?.fields.Modified,
      numberEdit: item?.fields.numberEdit,
      diagramJson: item?.fields.diagramJson,
      editorVersiona: item?.fields.editorVersiona,
      publishVersion: item?.fields.publishVersion,
      prepareVersion: item?.fields.prepareVersion,
      isQuote: item?.fields.isQuote,
      UserPermissions: JSON.parse(item?.fields?.UserPermissions ?? "[]"),
    };

    return result;
  }
}
